import { solveRoutes, visitLocked, type Coord, type RouteStop, type Vehicle } from '@fieldops/shared'

import { minutesBetween } from './geometry.js'
import { coordsOf, estimateTravelMinutes, isLiveVisit, type TravelHop } from './travel.js'
import type { BoardVisit } from './types.js'

/**
 * A van that is driving in circles, and the reorder that would fix it.
 *
 * This used to be its own nearest-neighbour sort. It is now a single-technician
 * call into the same solver the route planner runs, and that is the point:
 * two algorithms meant the chip could offer a saving the planner would then
 * undo, and a dispatcher who was told twelve minutes by one and nine by the
 * other had no way to tell which was lying.
 *
 * What stays local is everything the board already knows and the server would
 * have to be asked for: the run comes from the loaded day, and the hops are the
 * same straight-line estimates the travel stripes draw, because this is a chip
 * on a row and not worth a round trip. The saving it quotes is therefore a
 * floor — planning the day properly can only do better, since it has traffic.
 */

export const ZIGZAG_MIN_SAVING_MINUTES = 12

export interface ZigzagMove {
  id: string
  startAt: Date
  endAt: Date
}

export interface ZigzagPlan {
  userId: string
  savingMinutes: number
  moves: ZigzagMove[]
}

function liveSorted(visits: BoardVisit[]): BoardVisit[] {
  return [...visits].filter(isLiveVisit).sort((a, b) => a.startAt.getTime() - b.startAt.getTime())
}

/**
 * Straight lines at an urban speed, ignoring the hour.
 *
 * The solver's signature is time-dependent because traffic is; this estimator
 * is not, and saying so here is better than pretending. A stop's stored
 * `travelMinutes` is deliberately not consulted — it measures the drive that
 * actually happened in the booked order, which is the order being questioned.
 */
function straightLineTravel(from: Coord, to: Coord) {
  const { minutes } = estimateTravelMinutes(from, to, null)
  return { minutes, metres: 0 }
}

/**
 * Whether the chip may move this booking at all.
 *
 * Not a confirmed one (decision 76) — the server would refuse the move, and a
 * chip that offers a saving it cannot deliver is worse than no chip. Not a
 * crew either (rule 28): moving it for one person's run moves it for the
 * teammate whose run nobody is looking at.
 */
function held(visit: BoardVisit): boolean {
  return visitLocked(visit.status) || visit.assignments.length > 1
}

function stopOf(visit: BoardVisit, userId: string, anchored: boolean): RouteStop {
  const pinned = held(visit)
  return {
    id: visit.id,
    jobId: visit.job.id,
    coord: coordsOf(visit),
    serviceMinutes: Math.max(5, minutesBetween(visit.startAt, visit.endAt)),
    anchored,
    lockDay: pinned,
    lockTime: pinned,
    lockTech: pinned,
    startAt: visit.startAt,
    endAt: visit.endAt,
    userId,
    priority: 0,
    dueAt: null,
    scheduleGroupId: null,
    // The solver can route a van past the office to restock; the chip never
    // asks it to.
    restockUnits: 0,
    requiresPickup: false,
  }
}

/**
 * Whether this van's remaining stops are worth reordering, and the moves that
 * would do it. Null when there is nothing to save, or not enough of a run.
 */
export function zigzagPlan(visits: BoardVisit[], userId: string, now = new Date()): ZigzagPlan | null {
  const live = liveSorted(visits)
  if (live.length < 3) return null

  // The last thing that has happened or is happening pins the run: everything
  // after it can be resequenced, everything up to it is history.
  const fixed = [...live].reverse().find((visit) => visit.endAt <= now || visit.status === 'ON_SITE')
  const remaining = live.filter((visit) => (fixed ? visit.startAt >= fixed.endAt : true) && visit.endAt > now)

  if (remaining.length < 3) return null
  if (remaining.filter((visit) => coordsOf(visit)).length < 3) return null

  const first = remaining[0]!
  const last = remaining[remaining.length - 1]!

  const stops: RouteStop[] = [
    ...(fixed && fixed !== first ? [stopOf(fixed, userId, true)] : []),
    ...remaining.map((visit) => stopOf(visit, userId, false)),
  ]

  const vehicle: Vehicle = {
    userId,
    // No home. The chip asks "is this order silly", not "where should the day
    // start and end" — anchoring it to a driveway would quote a saving that
    // includes a commute nobody is being offered a change to.
    home: null,
    capacityUnits: null,
    shiftStart: fixed && fixed !== first ? fixed.startAt : first.startAt,
    // Generous on purpose: the run is being resequenced within itself, and a
    // tight window would make the answer "it does not fit" rather than "this
    // is the better order".
    shiftEnd: new Date(last.endAt.getTime() + 4 * 3600_000),
    obstacles: [],
    scheduleGroupId: null,
  }

  const result = solveRoutes(stops, [vehicle], {
    travelAt: straightLineTravel,
    office: null,
    restockMinutes: 0,
  })

  const route = result.routes.find((entry) => entry.userId === userId)
  if (!route) return null

  const savingMinutes = result.metrics.baselineDriveMinutes - result.metrics.driveMinutes
  if (savingMinutes < ZIGZAG_MIN_SAVING_MINUTES) return null

  const movable = new Set(remaining.filter((visit) => !held(visit)).map((visit) => visit.id))
  const moves: ZigzagMove[] = route.stops.flatMap((stop) =>
    stop.kind === 'job' && stop.moved && movable.has(stop.id)
      ? [{ id: stop.id, startAt: stop.startAt, endAt: stop.endAt }]
      : [],
  )

  if (moves.length === 0) return null

  return { userId, savingMinutes, moves }
}

export function zigzagChipLabel(plan: Pick<ZigzagPlan, 'savingMinutes'>): string {
  return `Zig-zag −${plan.savingMinutes}m`
}

export type { TravelHop }
